import { useEffect, useState,useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import axios from "../../../utils/axios";

// ================= 🔹 Info Row =================
const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" sx={{ py: 0.5 }}>
    <Typography fontSize={13} color="text.secondary">
      {label}
    </Typography>
    <Typography fontSize={13} fontWeight={500}>
      {value || "-"}
    </Typography>
  </Box>
);

const SectionTitle = ({ title }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      color: "#1976d2",
      display: "block",
      mb: 1,
    }}
  >
    {title}
  </Typography>
);



// ================= 🔹 MAIN COMPONENT =================
const FdrViewModal = ({ open, handleClose, data, refreshList }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    actionType: "RENEWED",
    newMaturityDate: "",
    newStartDate: "",
    newTaxRate: "",
    newTaxAmount: "0",
    newInterestRate: "",
    newTaxBasis: "",
    exciseDuty: "",
    bankCharge: "",
    grossInterest: "",
    netInterest: "",
      capitalizedInterest: "",
      capitalizedPrincipal: ""
  });
  const [errors, setErrors] = useState({});
  const handleModalClose = () => {
  setIsEditMode(false); // 👈 reset edit mode
  setFormData({}); // optional reset
  handleClose();        // 👈 existing close function
};


const debounceRef = useRef(null);

useEffect(() => {
  // required values
  const taxAmount = formData.newTaxAmount;
  const netInterest = formData.grossInterest;

  // don't call if empty
  if (!taxAmount || !netInterest) return;

  // clear previous timer
  if (debounceRef.current) {
    clearTimeout(debounceRef.current);
  }

  // set new timer
  debounceRef.current = setTimeout(async () => {
    try {
      const res = await axios.post("/fdr/get-tax-rate", {
        operation: formData.actionType,
        fdrAccountId: data?.id,
        taxAmount: Number(taxAmount),
        grossAmount: Number(netInterest),
        exciseDuty: Number(formData.exciseDuty || 0),
        bankCharge: Number(formData.bankCharge || 0),
      });

      const apiStatus = res?.data?.payload?.status;

      if (apiStatus === "success") {
        
        const taxRate = res?.data?.payload?.data?.taxRate || "";
        const grossInterest = res?.data?.payload?.data?.grossInterest || "";
        const netInterest = res?.data?.payload?.data?.netInterest || "";
        const capitalizedInterest = res?.data?.payload?.data?.capitalizedInterest || "";
        const capitalizedPrincipal = res?.data?.payload?.data?.capitalizedPrincipal || "";

        setFormData((prev) => ({
          ...prev,
          actionType: prev.actionType,
          newTaxRate: taxRate,
          grossInterest: grossInterest,
          netInterest: netInterest,
          capitalizedInterest: capitalizedInterest,
          capitalizedPrincipal: capitalizedPrincipal,
        }));
      }
    } catch (error) {
      console.error("Tax rate API failed", error);
    }
  }, 900);

  // cleanup
  return () => clearTimeout(debounceRef.current);
}, [formData.actionType,formData.newTaxAmount, formData.grossInterest, formData.exciseDuty, formData.bankCharge,  data?.id]);

  // 🔹 Prefill data
  useEffect(() => {
    if (data) {
      setFormData({
        actionType: "RENEWED",
        newMaturityDate: "",
        newStartDate: data?.maturityDate || "",
        newTaxRate: data?.taxRate || "",
        newTaxAmount: data?.taxamount || "0",
        newInterestRate: data?.interestRate || "",
        newTaxBasis: data?.interestBasis || "",
        exciseDuty: data?.exciseDuty || "",
        bankCharge: "",
        grossInterest: data?.gross_interest || "",
        netInterest: data?.net_interest || "",
        capitalizedInterest: data?.capitalizedInterest || "",
      });
    }
  }, [data]);

const isClosed = formData.actionType === "CLOSED";
const isPrematured = formData.actionType === "PREMATURED";
const isCapitalized = formData.actionType === "CAPITALIZED";
const today = new Date().toISOString().split("T")[0];



const minDate =
  formData.actionType === "PREMATURED"
    ? formData.newStartDate
     : formData.actionType === "RENEWED"
     ? formData.newStartDate
    : undefined;

   // console.log(" Sart Date",formData.newStartDate)
    // console.log("Maturity Date",formData.newMaturityDate)


const handleChange = (e) => {
  const { name, value } = e.target;

  // ✅ When actionType changes → always clear date
  if (name === "actionType") {
    setFormData((prev) => ({
      ...prev,
      actionType: value,
      newMaturityDate: "", // always reset
      newStartDate: value === "RENEWED" ? data?.maturityDate || "" : "",
    }));

    setErrors((prev) => ({
      ...prev,
      newMaturityDate: "",
    }));

    return;
  }

  if (name === "newMaturityDate") {
    const selectedDate = new Date(value);
    const today = new Date();
    const startDate = new Date(formData.startDate);
    const newMaturityDate = new Date(formData.newMaturityDate);

    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
   
   
    // ✅ Rule 1: no future date if PREMATURED
    if (
      formData.actionType === "PREMATURED" &&
      selectedDate > today
    ) {
      setErrors((prev) => ({
        ...prev,
        newMaturityDate: "Future date not allowed",
      }));
      return;
    }

    // ✅ Rule 2: must be greater than startDate if PREMATURED
    if (
      formData.actionType === "PREMATURED" &&
      selectedDate <= startDate
    ) {
      setErrors((prev) => ({
        ...prev,
        newMaturityDate: "Date must be after start date",
      }));
      return;
    }
 


    // ✅ valid
    setErrors((prev) => ({
      ...prev,
      newMaturityDate: "",
    }));

    setFormData((prev) => ({
      ...prev,
      newMaturityDate: value,
    }));

    return;
  }

  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));
};
  const isFormValid = () => {
  return (
    formData.actionType &&
    // formData.newMaturityDate &&
    // formData.newStartDate &&
    // formData.newTaxRate !== "" &&
    formData.newTaxAmount !== "" &&
    formData.newInterestRate !== "" &&
    formData.newTaxBasis &&
    formData.exciseDuty !== "" &&
    formData.bankCharge !== "" &&
    formData.grossInterest !== "" &&
    formData.netInterest !== ""
  );
};

const handleUpdate = async () => {
  const confirm = await Swal.fire({
    customClass: { container: "my-swal" },
    title: "Are you sure?",
    text: "Do you want to update this FDR?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, Update",
  });

  if (!confirm?.isConfirmed) return;

  try {
    const payload = {
      actionType: formData?.actionType,
      fdrAccountId: data?.id,
      newMaturityDate: formData?.newMaturityDate,
      newStartDate: formData?.newStartDate,
      newTaxRate: Number(formData?.newTaxRate || 0),
      newTaxAmount: Number(formData?.newTaxAmount || 0),
      newInterestRate: Number(formData?.newInterestRate || 0),
      newTaxBasis: formData?.newTaxBasis,
      exciseDuty: Number(formData?.exciseDuty || 0),
      bankCharge: Number(formData?.bankCharge || 0),
      grossInterest: Number(formData?.grossInterest || 0),
      netInterest: Number(formData?.netInterest || 0),
      newPrincipalAmount: Number(formData?.capitalizedPrincipal || 0),
    };

    const res = await axios.put("/fdr/update", payload);
   console.log(payload.newPrincipalAmount)
    const status = res?.data?.payload?.status;
    const message = res?.data?.payload?.message;

    // ❌ FAILED CASE
    if (status === "failed") {
      return Swal.fire({
        customClass: { container: "my-swal" },
        icon: "error",
        title: message || "Update failed",
      });
    }

    // ✅ SUCCESS CASE
    await Swal.fire({
      customClass: { container: "my-swal" },
      icon: "success",
      title: message || "FDR updated successfully",
    });

    handleClose();
    refreshList();

  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong";

    Swal.fire({
      customClass: { container: "my-swal" },
      icon: "error",
      title: errorMessage,
    });
  }
};


const InfoRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    sx={{
      py: 0.8,
      borderBottom: "1px dashed #eee",
    }}
  >
    <Typography fontSize={13} color="text.secondary">
      {label}
    </Typography>

    <Typography fontSize={13} fontWeight={600} color="#2c3e50">
      {value || "-"}
    </Typography>
  </Box>
);
const SectionTitle = ({ title }) => (
  <Typography
    variant="subtitle2"
    sx={{
      fontWeight: 700,
      color: "#1976d2",
      mb: 1.5,
      borderLeft: "3px solid #1976d2",
      pl: 1,
    }}
  >
    {title}
  </Typography>
);

  if (!data) return null;

  return (
    <Dialog open={open} onClose={handleModalClose} maxWidth="md" fullWidth>

      <DialogContent dividers>
        {/* ================= BASIC INFO ================= */}
      <Box
  sx={{
    border: "1px solid #e0e0e0",
    borderRadius: 2,
    p: 3,
    mb: 2,
    backgroundColor: "#f4f7fb",
  }}
>
  <Typography
    variant="h6"
    fontWeight={700}
    sx={{ mb: 3, color: "#1a2b4c" }}
  >
    FDR Details Overview
  </Typography>

  <Grid container spacing={3}>
    {/* ================= GENERAL INFO ================= */}
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          background: "#fff",
          p: 2,
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <SectionTitle title="General Information" />

        <InfoRow label="FDR Number" value={data?.fdrNumber} />
        <InfoRow label="Bank Name" value={data?.bankName} />
        <InfoRow
          label="Branch"
          value={
            data?.branchName
              ? `${data.branchName} (${data?.routingNo})`
              : "-"
          }
        />
        <InfoRow
          label="Principal Amount"
          value={data?.principalAmount ? Number(data.principalAmount).toLocaleString("en-IN") : "0"}
        />
        <InfoRow
          label="Interest Rate"
          value={`${data?.interestRate}% (${data?.interestBasis})`}
        />
      </Box>
    </Grid>

    {/* ================= DATE & STATUS ================= */}
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          background: "#fff",
          p: 2,
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <SectionTitle title="Date & Status" />

        <InfoRow
          label="Start Date"
          value={
            data?.startDate
              ? dayjs(data.startDate).format("DD MMM YYYY")
              : "-"
          }
        />
        <InfoRow
          label="Maturity Date"
          value={
            data?.maturityDate
              ? dayjs(data.maturityDate).format("DD MMM YYYY")
              : "-"
          }
        />
        <InfoRow
          label="Remaining Days"
          value={data?.remainingdays}
        />
        <InfoRow label="Status" value={data?.status} />
        <InfoRow
          label="Maturity Status"
          value={data?.maturityStatus}
        />
      </Box>
    </Grid>
  </Grid>

  {/* ================= FINANCIAL SUMMARY ================= */}
  <Box
    mt={2}
    sx={{
      background: "#fff",
      p: 2,
      borderRadius: 2,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    }}
  >
    <SectionTitle title="Financial Summary" />

    <Grid container spacing={12}>
      <Grid item xs={12} md={4}>
        <InfoRow
          label="Gross Interest"
          value={data?.gross_interest ? Number(data.gross_interest).toLocaleString("en-IN") : "0"}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <InfoRow
          label="Net Interest"
          value={data?.net_interest ? Number(data.net_interest).toLocaleString("en-IN") : "0"}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <InfoRow
          label="Tax Amount"
          value={data?.taxamount ? Number(data.taxamount).toLocaleString("en-IN") : "0"}
        />
      </Grid>
    </Grid>
  </Box>
</Box>
        {/* ================= UPDATE FORM ================= */}

{isEditMode && (
  <Box sx={{ background: "#fff", p: 2, borderRadius: 2 }}>
    <SectionTitle title="Update FDR" />

    <Grid container spacing={2}>
        {/* Action Type */}
{/* Action Type */}
<Grid item xs={4}>
  <TextField
    select
    fullWidth
    label="Action Type"
    name="actionType"
    value={formData.actionType}
    onChange={handleChange}
  >
    <MenuItem value="RENEWED">Renewed</MenuItem>
    <MenuItem value="PREMATURED">Prematured</MenuItem>
    <MenuItem value="CLOSED">Closed</MenuItem>
    <MenuItem value="CAPITALIZED">Capitalized</MenuItem>
  </TextField>
</Grid>

{/* Start Date (ONLY for RENEWED) */}
    {!isClosed && !isPrematured && !isCapitalized && (
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Start Date"
          value={formData.newStartDate}
          // InputProps={{ readOnly: true }}
          disabled
        />
      </Grid>
    )}
{/* Capitalized Interest (ONLY for RENEWED) */}
    {!isClosed && isCapitalized && (
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Capitalized Interest"
          name="capitalizedInterest"
          value={formData.capitalizedInterest}
          onChange={handleChange}
            disabled
        />
      </Grid>
      
    )}

        {!isClosed && isCapitalized && (
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Capitalized Principal"
          name="capitalizedPrincipal"
          value={formData.capitalizedPrincipal}
          onChange={handleChange}
           disabled
        />
      </Grid>
      
    )}

{/* Next Maturity Date (for RENEWED + PREMATURED) */}
            {!isClosed && !isCapitalized && (
            <Grid item xs={4}>
          <TextField
          fullWidth
          label="Next Maturity Date"
          type="date"
          name="newMaturityDate"
          value={formData.newMaturityDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        inputProps={{
          min: minDate,
          max:
            formData.actionType === "PREMATURED"
              ? today
              : undefined,
        }}
          error={!!errors.newMaturityDate}
          helperText={errors.newMaturityDate}
        />
        </Grid>
            )}  
          
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Gross Interest"
                name="grossInterest"
                value={formData.grossInterest}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="New Tax Amount"
                name="newTaxAmount"
                value={formData.newTaxAmount}
                onChange={handleChange}
              />
            </Grid>


           {!isClosed && !isCapitalized && (
          
            <Grid item xs={4}>
                      <TextField
              fullWidth
              label="New Tax Rate"
              name="newTaxRate"
              value={formData.newTaxRate}
              // InputProps={{ readOnly: true }}
              disabled
            />
            </Grid>
            )}



            <Grid item xs={4}>
              <TextField
                fullWidth
                label="New Interest Rate"
                name="newInterestRate"
                value={formData.newInterestRate}
                onChange={handleChange}
              />
            </Grid>
             {!isClosed && !isCapitalized && (
            <Grid item xs={4}>
              <TextField
                select
                fullWidth
                label="Tax Basis"
                name="newTaxBasis"
                value={formData.newTaxBasis}
                onChange={handleChange}
              >
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="DAILY">Daily</MenuItem>
                <MenuItem value="YEARLY">Yearly</MenuItem>
              </TextField>
            </Grid>
)}
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Excise Duty"
                name="exciseDuty"
                value={formData.exciseDuty}
                onChange={handleChange}
              />
            </Grid>


           {!isClosed && !isCapitalized && (
            <Grid item xs={4}>
              <TextField
                type="number"
                fullWidth
                label="Bank Charge"
                name="bankCharge"
                value={formData.bankCharge}
                onChange={handleChange}
              />
            </Grid>
)}

              {!isClosed && !isCapitalized && (
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Net Interest"
                name="netInterest"
                value={formData.netInterest}
                onChange={handleChange}
                // InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>

           )}
          </Grid>
  </Box>
)}

       
      </DialogContent>

          <DialogActions>
      {!isEditMode && (
  <Button
    variant="outlined"
    onClick={() => setIsEditMode(true)}
    // when status is not ACTIVE or CAPITALIZED → no edit allowed
    disabled={!(data?.status === "ACTIVE" || data?.status === "CAPITALIZED")}

  >
    Update
  </Button>
)}

        <Button onClick={handleModalClose}>Cancel</Button>

        {isEditMode && (
          <Button
            variant="contained"
            onClick={handleUpdate}
            // disabled={!isFormValid()}
          >
            Update FDR
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FdrViewModal;